import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { TypeText } from '@mui/material/styles';

interface textType extends TypeText {
  additionalText: string;
}

let lightTheme = createTheme({
  palette: {
    primary: {
      main: "#26B6F6",
      light: "#E3EFFF",
      dark: "#1298eD",
      medium: "#CBDDF6",
      contrastText: "#FFFFFF",
      lighten: "#f6fcff",
    },
    secondary: {
      main: "#89CC45",
      light: "#E9FAE1",
      dark: "#618833",
      contrastText: "#FFFFFF",
    },
    background: {
      paper: "#FFFFFF",
      default: "#F6F5Fb",
    },
    text: {
      primary: "#1F1F1F",
      secondary: "#666666",
      disabled: "#DDDDDD",
      additionalText: "#6E798C",
    } as textType,
    success: {
      main: "#89CC45",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#FCB61E",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#F2133C",
      contrastText: "#FFFFFF",
    },
    action: {
      selected: "#F6FCFF",
    },
    greyDesign: {
      surface2: "#7E7D82",
      separator: "#F6F5FB",
      onSurface3: "#E4E1E1",
    },
    status: {
      toDo: "#26B6F6",
      inProgress: "#9874fe",
      done: "#8ec549",
      cancelled: "#F2133C"
    }
  },
  // v4 breakpoint values; MUI 5+ defaults (900/1200/1536) would move every responsive layout.
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
  components: {
    // Material UI 4 drew text fields, selects and form controls with the "standard" (underline) variant.
    MuiTextField: { defaultProps: { variant: "standard" } },
    MuiSelect: { defaultProps: { variant: "standard" } },
    MuiFormControl: { defaultProps: { variant: "standard" } },
    MuiListItemButton: {
      // Replaces v4's <ListItem button>, which did not grow inside flex columns.
      styleOverrides: { root: { flexGrow: 0 } },
    },
    MuiGrid: {
      styleOverrides: {
        // Material UI 4 grid containers filled their parent's width; the new Grid only does when given a size.
        root: { "&.MuiGrid-container:not([class*='MuiGrid-grid-'])": { width: "100%" } },
      },
    },
    MuiMenu: {
      defaultProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      },
      styleOverrides: {
        paper: {
          maxHeight: "285px",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: "100%",
          "-webkit-overflow-scrolling": "touch",
        },
        body: {
          height: "100%",
        },
        button: {
          background: "none",
        },
        a: {
          textDecoration: "none",
        },

        "input, select, textarea": {
          fontFamily: "Roboto, sans-serif",
        },
      },
    },
    // Material UI 4 underlined links only on hover.
    MuiLink: { defaultProps: { underline: "hover" } },
    MuiButton: {
      // Material UI 4 buttons without a color were neutral, not primary.
      defaultProps: { color: "inherit" },
      styleOverrides: {
        root: {
          textTransform: "capitalize",
          padding: "4px 16px",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          padding: "12px",
          paddingTop: "4px",
          paddingBottom: "4px",
          height: "2.5rem",
          color: "#666666",

          "&.Mui-selected": {
            color: "#26B6F9",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          "&.Mui-error": {
            "&:after": {
              borderBottomWidth: "1px",
            },
          },
          "&:hover": {
            "&:not(.Mui-disabled)": {
              "&:before": {
                borderBottomWidth: "1px",
              },
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          transform: "translate(0, 6px) scale(0.815)",
          color: "#26B6F9",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          position: "absolute",
          left: 0,
          top: "100%",
          lineHeight: "1.26",
          fontSize: "0.625rem",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: "30px",
        },
      },
    },
  },
});

lightTheme = responsiveFontSizes(lightTheme);
export default lightTheme;
